import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import './BausteinPickerDialog.css';

/**
 * Auswahl eines Bausteins beim Einfügen.
 *
 * Angefordert wird der Dialog aus der Zeichenfläche heraus — der Provider
 * dort kennt React nicht und wartet auf ein Versprechen. Darum bekommt der
 * Dialog seine Liste und eine `onWahl` von außen; er entscheidet nichts
 * selbst.
 *
 * Zeigt je Baustein, wie oft er schon aufgerufen wird. Das ist die Angabe,
 * nach der man beim Einfügen sucht: Ein Baustein mit vielen Aufrufen ist
 * erprobt, einer mit keinem womöglich ein Überbleibsel.
 */
const BausteinPickerDialog = ({ isOpen, bausteine, art, onWahl, onAbbruch }) => {
  const [filter, setFilter] = useState('');
  const [markiert, setMarkiert] = useState(0);
  const eingabeRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setFilter('');
      setMarkiert(0);
      // Auf den Filter setzen: Bei wenigen Bausteinen tippt niemand, bei
      // vielen ist es der erste Griff.
      setTimeout(() => eingabeRef.current?.focus(), 0);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const suche = filter.trim().toLowerCase();
  const gefiltert = suche
    ? bausteine.filter((b) => b.name.toLowerCase().includes(suche))
    : bausteine;

  const waehlen = (baustein) => { if (baustein) onWahl(baustein); };

  const beiTaste = (e) => {
    if (e.key === 'Escape') { onAbbruch(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setMarkiert((i) => Math.min(i + 1, gefiltert.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setMarkiert((i) => Math.max(i - 1, 0)); }
    if (e.key === 'Enter') { e.preventDefault(); waehlen(gefiltert[markiert]); }
  };

  const alsKopie = art === 'kopie';

  return (
    <div className="baustein-picker-overlay">
      <button type="button" className="baustein-picker-overlay-btn" onClick={onAbbruch} aria-label="Dialog schließen" />
      <dialog className="baustein-picker-dialog" open aria-label="Baustein auswählen" onKeyDown={beiTaste}>
        <div className="baustein-picker-header">
          <span className="bp-icon"><i className={alsKopie ? 'bi bi-files' : 'bi bi-link-45deg'}></i></span>
          <div>
            <h5>Baustein einfügen</h5>
            <p className="bp-untertitel">
              {alsKopie
                ? 'Als Kopie — der Inhalt wird übernommen und ist danach eigenständig.'
                : 'Als Verweis — spätere Änderungen am Baustein wirken hier mit.'}
            </p>
          </div>
        </div>

        <div className="baustein-picker-body">
          <input
            ref={eingabeRef}
            type="text"
            className="bp-filter"
            value={filter}
            onChange={(e) => { setFilter(e.target.value); setMarkiert(0); }}
            placeholder="Nach Namen filtern…"
            autoComplete="off"
          />

          {gefiltert.length === 0 ? (
            <div className="bp-leer">Kein Baustein passt zum Filter.</div>
          ) : (
            <ul className="bp-liste">
              {gefiltert.map((baustein, i) => (
                <li key={baustein.id}>
                  <button
                    type="button"
                    className={`bp-eintrag ${i === markiert ? 'bp-eintrag-aktiv' : ''}`}
                    onClick={() => waehlen(baustein)}
                    onMouseEnter={() => setMarkiert(i)}
                  >
                    <span className="bp-name" title={baustein.id}>{baustein.name}</span>
                    <span
                      className={`bp-aufrufe ${baustein.aufrufe === 0 ? 'bp-ungenutzt' : ''}`}
                      title={baustein.aufrufe === 0
                        ? 'Wird bisher nirgends aufgerufen'
                        : `${baustein.aufrufe} Aufrufstelle(n)`}
                    >
                      {baustein.aufrufe}×
                    </span>
                    <span className="bp-elemente">{baustein.elemente} Elemente</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="baustein-picker-footer">
          <span className="bp-hinweis">{gefiltert.length} von {bausteine.length}</span>
          <button type="button" className="btn bp-btn-abbrechen" onClick={onAbbruch}>Abbrechen</button>
        </div>
      </dialog>
    </div>
  );
};

BausteinPickerDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  bausteine: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    aufrufe: PropTypes.number,
    elemente: PropTypes.number,
  })),
  art: PropTypes.oneOf(['verweis', 'kopie']),
  onWahl: PropTypes.func.isRequired,
  onAbbruch: PropTypes.func.isRequired,
};

BausteinPickerDialog.defaultProps = { bausteine: [], art: 'verweis' };

export default BausteinPickerDialog;
